import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getApi } from '../Services/apiService';
import { useLocation } from 'react-router-dom';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [readCount, setReadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'unread', 'read', or 'all'
  
  // Dashboard specific state
  const [dashboardNotifications, setDashboardNotifications] = useState([]);
  const [dashboardPage, setDashboardPage] = useState(1);
  const [dashboardHasMore, setDashboardHasMore] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const prevUnreadCountRef = useRef(null);
  const isInitialMount = useRef(true);
  const hasFetchedInitial = useRef(false);
  const location = useLocation();
  const { pathname } = location;

  const fetchNotifications = useCallback(async (page = 1, isRefresh = false, tab = null) => {
    try {
      if (page === 1 && !isRefresh) {
        setLoading(true);
      }
      
      const token = JSON.parse(localStorage.getItem('token'));
      if (!token) return;

      const targetTab = tab || activeTab;
      const params = new URLSearchParams();
      params.append('per_page', 20);
      params.append('page', page);
      params.append('status', targetTab);

      const response = await getApi(`/cell-remark/my-remarks?${params.toString()}`);
      
      if (response.success) {
        const paginationData = response.data.remarks;
        const remarks = paginationData.data || [];
        const counts = response.data.counts || {};
        
        // Update counts
        setUnreadCount(counts.unread || 0);
        setReadCount(counts.read || 0);

        // If we fetched "all", we can also update the dashboard state
        if (targetTab === 'all' && page === 1) {
          setDashboardNotifications(remarks);
          setDashboardPage(1);
          setDashboardHasMore((paginationData.current_page || 1) < (paginationData.last_page || 1));
        }

        // Update notifications (dropdown)
        // If it was a poll (isRefresh && targetTab === 'all'), and we are currently on 'unread' or 'read' tab, 
        // we should filter the results locally to keep the UI consistent without extra API calls
        if (isRefresh && targetTab === 'all' && activeTab !== 'all') {
          const filtered = remarks.filter(n => activeTab === 'unread' ? !n.isRead : n.isRead);
          setNotifications(filtered);
        } else if (isRefresh || page === 1) {
          setNotifications(remarks);
        }
        
        // Update pagination info for dropdown
        if (page === 1 || !isRefresh) {
           setCurrentPage(paginationData.current_page || 1);
           setHasMore((paginationData.current_page || 1) < (paginationData.last_page || 1));
        }
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      if (!isRefresh) setLoading(false);
    }
  }, [activeTab]);

  const loadMoreNotifications = useCallback(async () => {
    if (!hasMore || loading) return;
    
    try {
      const token = JSON.parse(localStorage.getItem('token'));
      if (!token) return;

      const nextPage = currentPage + 1;
      
      const params = new URLSearchParams();
      params.append('per_page', 20);
      params.append('page', nextPage);
      params.append('status', activeTab);

      const response = await getApi(`/cell-remark/my-remarks?${params.toString()}`);
      
      if (response.success) {
        const paginationData = response.data.remarks;
        const newRemarks = paginationData.data || [];
        
        // Append new remarks to existing ones
        setNotifications(prev => [...prev, ...newRemarks]);
        
        // Update pagination info
        setCurrentPage(paginationData.current_page || nextPage);
        setHasMore((paginationData.current_page || nextPage) < (paginationData.last_page || 1));
      }
    } catch (error) {
      console.error('Error loading more notifications:', error);
    }
  }, [activeTab, currentPage, hasMore, loading]);

  const loadMoreDashboardNotifications = useCallback(async () => {
    if (!dashboardHasMore || dashboardLoading) return;
    
    try {
      const token = JSON.parse(localStorage.getItem('token'));
      if (!token) return;

      setDashboardLoading(true);
      const nextPage = dashboardPage + 1;
      
      const params = new URLSearchParams();
      params.append('per_page', 20);
      params.append('page', nextPage);
      params.append('status', 'all');

      const response = await getApi(`/cell-remark/my-remarks?${params.toString()}`);
      
      if (response.success) {
        const paginationData = response.data.remarks;
        const newRemarks = paginationData.data || [];
        
        setDashboardNotifications(prev => [...prev, ...newRemarks]);
        setDashboardPage(paginationData.current_page || nextPage);
        setDashboardHasMore((paginationData.current_page || nextPage) < (paginationData.last_page || 1));
      }
    } catch (error) {
      console.error('Error loading more dashboard notifications:', error);
    } finally {
      setDashboardLoading(false);
    }
  }, [dashboardPage, dashboardHasMore, dashboardLoading]);

  const changeTab = useCallback((tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    // We don't clear notifications immediately to avoid flicker, fetchNotifications will replace them
  }, []);

  const refreshNotifications = useCallback(() => {
    fetchNotifications(1, true, 'all');
  }, [fetchNotifications]);

  // Initial fetch and auto-refresh every 60 seconds
  useEffect(() => {
    // Initial fetch is now handled by the pathname useEffect to ensure it happens right after login
    const interval = setInterval(() => {
      fetchNotifications(1, true, 'all'); // Always poll 'all'
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Fetch notifications when navigating to admin routes (e.g. after login)
  useEffect(() => {
    if (pathname.startsWith('/admin') && !hasFetchedInitial.current) {
      const token = JSON.parse(localStorage.getItem('token'));
      if (token) {
        fetchNotifications(1, false, 'all');
        hasFetchedInitial.current = true;
      }
    } else if (pathname === '/' || pathname === '/login') {
      hasFetchedInitial.current = false;
    }
  }, [pathname, fetchNotifications]);

  // Refetch when tab changes
  useEffect(() => {
    if (!isInitialMount.current) {
      fetchNotifications(1, false, activeTab);
    }
  }, [activeTab, fetchNotifications]);

  // Handle toast notification timing refs
  useEffect(() => {
    if (unreadCount !== null) {
      // First time we get counts, set the ref but don't toggle mount flag yet
      if (prevUnreadCountRef.current === null) {
        prevUnreadCountRef.current = unreadCount;
        return;
      }
      
      // Update ref ONLY after a render where counts might have been compared
      // The CellRemarkNotifications component will see the old ref value in its own useEffect
      // and we update it here for the NEXT change detection.
      const timer = setTimeout(() => {
        prevUnreadCountRef.current = unreadCount;
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [unreadCount]);

  useEffect(() => {
    // Set initial mount to false after first successful load or just after delay
    const timer = setTimeout(() => {
      isInitialMount.current = false;
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const [isNotificationOpen, setIsNotificationOpen] = useState(false);

  const value = {
    notifications,
    unreadCount,
    readCount,
    loading,
    currentPage,
    hasMore,
    activeTab,
    dashboardNotifications,
    dashboardLoading,
    dashboardHasMore,
    fetchNotifications,
    loadMoreNotifications,
    loadMoreDashboardNotifications,
    changeTab,
    refreshNotifications,
    prevUnreadCountRef,
    isInitialMount,
    isNotificationOpen,
    setIsNotificationOpen
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
